import { create } from 'zustand';
import { signIn, signOut, fetchAuthSession, getCurrentUser, confirmSignIn } from 'aws-amplify/auth';
import { queryClient } from '../lib/queryClient';
import { recordLogin } from '../api/endpoints';

type SignInStep = 'DONE' | 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED';

interface AuthState {
  isAuthenticated: boolean;
  user: { sub: string; email: string; groups: string[] } | null;
  isLoading: boolean;
  nextStep: SignInStep | null;
  login: (email: string, password: string) => Promise<SignInStep>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  user: null,
  isLoading: true,
  nextStep: null,

  login: async (email, password) => {
    // Clear any stale auth state before attempting sign-in.
    // On iOS 18, leftover tokens from previous sessions can cause
    // "A network error has occurred" because Amplify tries to refresh
    // expired tokens before initiating a new sign-in.
    try { await signOut(); } catch {
      // signOut itself may fail with a network error when trying to revoke
      // stale tokens. In that case, purge Amplify's localStorage entries
      // so the SDK starts fresh.
      try {
        Object.keys(localStorage)
          .filter(k => k.startsWith('CognitoIdentityServiceProvider.') || k.startsWith('amplify-'))
          .forEach(k => localStorage.removeItem(k));
      } catch { /* localStorage may be unavailable */ }
    }

    const result = await signIn({ username: email, password });
    const step = result.nextStep?.signInStep as SignInStep;

    if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      set({ nextStep: step });
      return step;
    }

    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload;
    set({
      isAuthenticated: true,
      nextStep: null,
      user: {
        sub: (payload?.sub as string) ?? '',
        email: (payload?.email as string) ?? email,
        groups: (payload?.['cognito:groups'] as string[]) ?? [],
      },
    });
    sessionStorage.setItem('login-recorded', '1');
    recordLogin().catch(() => {});
    return 'DONE';
  },

  completeNewPassword: async (newPassword: string) => {
    const result = await confirmSignIn({ challengeResponse: newPassword });
    if (result.isSignedIn) {
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload;
      set({
        isAuthenticated: true,
        nextStep: null,
        user: {
          sub: (payload?.sub as string) ?? '',
          email: (payload?.email as string) ?? '',
          groups: (payload?.['cognito:groups'] as string[]) ?? [],
        },
      });
      sessionStorage.setItem('login-recorded', '1');
      recordLogin().catch(() => {});
    }
  },

  logout: async () => {
    await signOut();
    queryClient.clear();
    sessionStorage.removeItem('login-recorded');
    set({ isAuthenticated: false, user: null, nextStep: null });
  },

  // ACHTUNG: checkAuth() darf NUR von AuthGate aufgerufen werden!
  // Niemals aus LoginPage oder anderen Pages – verursacht Endlosschleife.
  checkAuth: async () => {
    set({ isLoading: true });
    try {
      await getCurrentUser();
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload;
      set({
        isAuthenticated: true,
        isLoading: false,
        user: {
          sub: (payload?.sub as string) ?? '',
          email: (payload?.email as string) ?? '',
          groups: (payload?.['cognito:groups'] as string[]) ?? [],
        },
      });
      // Session-Wiederherstellung als Login protokollieren (einmal pro Browser-Session)
      if (!sessionStorage.getItem('login-recorded')) {
        sessionStorage.setItem('login-recorded', '1');
        recordLogin().catch(() => {});
      }
    } catch {
      set({ isAuthenticated: false, isLoading: false, user: null });
    }
  },

  getToken: async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  },
}));
